/**
 * What every menu screen has in common: its clock, the starfield behind, the header with BACK, the
 * opening transition, music, screen-reader announcements and the back action. Screens extend it
 * and draw the rest.
 */

import type { Screen } from '../../render/screen';
import type { MenuAction, PointerInput, Scene, SceneHost, Services, UiSound } from '../scene';
import { drawBackdrop, menuStars } from './backdrop';
import { backHit, drawFooter, drawHeader, drawReveal, keyWords } from './chrome';
import { usesTouch } from './input';
import { setCalm } from './theme';

export abstract class UiScene implements Scene {
  /** Milliseconds since the screen was (re)entered. */
  protected ms = 0;
  /** The screen's name in the header; '' for none. */
  protected abstract readonly heading: string;
  /** The tune to play while this screen is up; null leaves the music alone. */
  protected readonly track: 'title' | 'hiscore' | null = 'title';
  /** The header's BACK button (screens that must be finished, such as Initials, hide it). */
  protected readonly showBack: boolean = true;
  /** How far the stars are dimmed behind this screen's text (0-1). */
  protected readonly veil: number = 0.5;
  private backHot = false;

  constructor(protected readonly host: SceneHost) {}

  protected get services(): Services {
    return this.host.services;
  }

  enter(): void {
    this.ms = 0;
    if (this.track) this.services.audio.music(this.track);
    this.say(this.describe());
  }

  update(dtMs: number): void {
    this.ms += dtMs;
    menuStars().update(dtMs, this.warp());
    this.tick(dtMs);
  }

  draw(screen: Screen): void {
    const o = this.services.options.get();
    setCalm(o.reduceFlashing);
    drawBackdrop(screen, o.deepStars, o.reduceFlashing, this.veil);
    this.paint(screen);
    if (this.heading) drawHeader(screen, this.heading, this.ms, this.showBack, this.backHot);
    // Fingers get their own hint: the keys' one would tell them nothing.
    const hint = this.touching() && !this.services.input.gamepad() ? this.touchHint() : this.hint();
    if (hint) drawFooter(screen, hint);
    drawReveal(screen, this.ms);
  }

  action(a: MenuAction): void {
    if (a === 'back') this.back();
    else this.onAction(a);
  }

  pointer(p: PointerInput): void {
    if (this.heading && this.showBack) {
      const hot = backHit(p.x, p.y);
      if (hot && p.kind === 'down' && p.button === 0) return this.back();
      if (p.kind === 'move') this.backHot = hot;
    }
    if (p.kind === 'down' && p.button === 2) return this.back();
    this.onPointer(p);
  }

  /** What to tell a screen reader on entering: the screen's name and the selected item. */
  protected describe(): string {
    return this.heading;
  }

  /** The footer hint, from the words for the input in use. */
  protected hint(): string {
    return '';
  }

  /** The footer hint for a player on touch controls with no pad plugged in. */
  protected touchHint(): string {
    return '';
  }

  /** The player is on touch controls (see `usesTouch`). */
  protected touching(): boolean {
    return usesTouch(this.services.input.touch(), this.services.options.get().touch);
  }

  /** How fast the stars run behind this screen (1 = their own pace). */
  protected warp(): number {
    return 1;
  }

  protected words(): { ok: string; back: string } {
    return keyWords(this.services.input.gamepad());
  }

  protected tick(_dtMs: number): void {}

  protected abstract paint(screen: Screen): void;

  protected abstract onAction(a: MenuAction): void;

  protected onPointer(_p: PointerInput): void {}

  protected back(): void {
    this.sound('back');
    this.host.back();
  }

  protected sound(s: UiSound): void {
    this.services.audio.ui(s);
  }

  protected say(text: string): void {
    if (text) this.services.announce(text);
  }
}
